"use client";

import React from "react";
import { ShoppingCart, Star } from "lucide-react";
import { EcommerceProductsProvider, useEcommerceProducts } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { AspectRatio } from "@/components/ui/aspect-ratio";
function ProductPrice({
  post
}: {
  post: any;
}) {
  const [data, setData] = React.useState<{
    price: number | null;
    oldPrice: number | null;
    discount: string;
    loaded: boolean;
  }>({
    price: null,
    oldPrice: null,
    discount: "",
    loaded: false
  });
  React.useEffect(() => {
    if (!post?.id) return;
    let isMounted = true;
    const load = async () => {
      try {
        const url = `/wp-json/wp/v2/product/${post.id}`;
        const res = await fetch(url, {
          credentials: "same-origin",
          cache: "no-store"
        });
        if (!res.ok) {
          throw new Error(`Erreur HTTP ${res.status}`);
        }
        const json = await res.json();
        const regularPrice = Number(json?.price ?? json?.meta?.price ?? json?.meta?._price ?? 0);
        const salePrice = Number(json?.discounted_price ?? json?.meta?._sale_price ?? 0);
        const pack1 = Number(json?.meta?.wvc_product_pack_1_price || 0);
        const discount = json?.meta?.wvc_product_pack_4_discount || "";
        if (!isMounted) return;
        if (salePrice > 0 && regularPrice > 0 && salePrice !== regularPrice) {
          setData({
            price: salePrice,
            oldPrice: regularPrice,
            discount,
            loaded: true
          });
        } else if (regularPrice > 0) {
          setData({
            price: regularPrice,
            oldPrice: null,
            discount,
            loaded: true
          });
        } else if (pack1 > 0) {
          setData({
            price: pack1,
            oldPrice: null,
            discount,
            loaded: true
          });
        } else {
          setData({
            price: null,
            oldPrice: null,
            discount: "",
            loaded: true
          });
        }
      } catch (error) {
        console.error("Erreur chargement prix produit :", post?.id, error);
        if (!isMounted) return;
        setData({
          price: null,
          oldPrice: null,
          discount: "",
          loaded: true
        });
      }
    };
    load();
    return () => {
      isMounted = false;
    };
  }, [post?.id]);
  if (!data.loaded) {
    return <div className="text-lg font-bold text-muted-foreground italic">
        Chargement...
      </div>;
  }
  if (!data.price) {
    return <div className="text-lg font-bold text-muted-foreground italic">
        Prix sur demande
      </div>;
  }
  return <div className="flex flex-col gap-1">
      <div className="flex items-baseline gap-2 flex-wrap">
        <div className="text-2xl font-extrabold text-chart-2">
          {data.price.toLocaleString("fr-FR")} FCFA
        </div>

        {data.oldPrice && data.oldPrice !== data.price && <div className="text-sm font-medium text-muted-foreground line-through">
            {data.oldPrice.toLocaleString("fr-FR")} FCFA
          </div>}
      </div>

      {data.discount && <div className="inline-block w-fit rounded-full bg-red-100 px-2 py-1 text-xs font-bold text-red-600">
          {data.discount} sur pack 4
        </div>}
    </div>;
}
function ProductsGrid() {
  const {
    posts,
    loading,
    error,
    hasNext,
    loadMore,
    isRefetching,
    currentPage,
    setPage
  } = useEcommerceProducts();
  React.useEffect(() => {
    const timer = window.setTimeout(() => {
      window.dispatchEvent(new Event("WVC_PRODUCTS_REFRESH"));
    }, 500);
    return () => window.clearTimeout(timer);
  }, []);
  const filteredPosts = posts.filter((post: any) => post?.categories?.some((cat: any) => cat?.slug === "capsules-tassimo" || cat?.slug === "capsules-dolce-gusto"));
  if (loading && filteredPosts.length === 0) {
    return <div data-section-id="156720" className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {[...Array(8)].map((_, i) => <Card key={i} data-index={i} className="overflow-hidden rounded-xl border border-border">
            <AspectRatio ratio={1}>
              <Skeleton className="h-full w-full rounded-none" />
            </AspectRatio>
            <CardContent className="flex flex-col gap-3 p-4">
              <Skeleton className="h-6 w-3/4" />
              <Skeleton className="h-4 w-1/4" />
              <Skeleton className="mt-4 h-8 w-1/2" />
            </CardContent>
            <CardFooter className="p-4 pt-0">
              <Skeleton className="h-12 w-full" />
            </CardFooter>
          </Card>)}
      </div>;
  }
  if (error) {
    return <div className="py-12 text-center text-destructive">
        <p>Échec du chargement des produits. Veuillez réessayer plus tard.</p>
      </div>;
  }
  if (filteredPosts.length === 0) {
    return <div className="py-12 text-center text-muted-foreground">
        <p>Aucun produit trouvé pour le moment.</p>
      </div>;
  }
  return <div className="space-y-10">
      {currentPage > 1 && <div className="flex justify-center">
          <Button size="lg" variant="outline" onClick={() => setPage(1)} disabled={isRefetching} className="min-w-[260px] rounded-xl border-2 border-border bg-background text-base font-bold text-foreground transition-all hover:bg-accent hover:text-chart-2">
            {isRefetching ? "Chargement..." : "Précédent"}
          </Button>
        </div>}

      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {filteredPosts.map((post, i) => {
        const rating = 5.0;
        const buyNowUrl = post.link;
        const image = post.featured_image_url || post.featuredImage || "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1592750/img/product_placeholder.png";
        const storeName = "KBC Coffee Service";
        return <Card key={post.id} data-index={i} className="group flex h-full flex-col overflow-hidden rounded-xl border border-border bg-card shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-md">
              <Link to={post.link || "#"} className="relative block overflow-hidden bg-white">
                <AspectRatio ratio={1}>
                  <img src={image} alt={post.title} className="h-full w-full object-contain p-6 transition-transform duration-500 group-hover:scale-110" />
                </AspectRatio>
              </Link>

              <CardContent className="flex flex-grow flex-col gap-2 p-5">
                <Link to={post.link || "#"} className="transition-colors group-hover:text-chart-2">
                  <h3 className="line-clamp-2 font-serif text-lg font-bold leading-tight text-foreground" dangerouslySetInnerHTML={{
                __html: post.title
              }} />
                </Link>

                <div className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground">
                  <div className="flex items-center">
                    {[...Array(5)].map((_, starIdx) => <Star key={starIdx} className={`h-4 w-4 ${starIdx < Math.floor(rating) ? "fill-chart-2 text-chart-2" : "fill-muted text-muted"}`} />)}
                  </div>
                  <span>{rating.toFixed(1)}</span>
                </div>

                <div className="mt-auto pt-4">
                  <span className="mb-1 block text-xs font-bold uppercase tracking-widest text-muted-foreground">
                    {storeName}
                  </span>
                  <ProductPrice post={post} />
                </div>
              </CardContent>

              <CardFooter className="p-5 pt-0">
                <Button asChild className="h-12 w-full rounded-lg bg-chart-2 text-base font-bold text-primary-foreground shadow-sm transition-all hover:bg-chart-2/90">
                  <Link to={buyNowUrl || "#"}>
                    Commander <ShoppingCart className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
              </CardFooter>
            </Card>;
      })}
      </div>

      {hasNext && <div className="flex justify-center pt-6">
          <Button size="lg" variant="outline" onClick={async () => await loadMore()} disabled={isRefetching} className="min-w-[260px] rounded-xl border-2 border-border bg-background text-base font-bold text-foreground transition-all hover:bg-accent hover:text-chart-2">
            {isRefetching ? "Chargement..." : "Voir plus de saveurs"}
          </Button>
        </div>}
    </div>;
}
export default function Products() {
  const wp_query = new WP_Query({
    per_page: 12,
    orderby: "date",
    order: "desc",
    tax_query: [{
      taxonomy: "product_cat",
      field: "slug",
      terms: ["capsules-tassimo", "capsules-dolce-gusto"]
    }]
  });
  return <section id="dolcegustoproducts" className="w-full bg-background py-16 text-foreground md:py-20 lg:py-28">
      <div className="container mx-auto px-4 md:px-6">
        <div className="mb-12 flex flex-col items-center justify-center space-y-4 text-center md:mb-16">
          <h2 className="font-serif text-3xl font-extrabold tracking-tight text-foreground sm:text-4xl md:text-6xl">
            Nos Capsules & Café
          </h2>
          <p className="max-w-[800px] font-sans text-muted-foreground md:text-xl/relaxed lg:text-lg/relaxed xl:text-xl/relaxed">
            Découvrez notre sélection exclusive de capsules Dolce Gusto et Tassimo, ainsi que nos machines premium pour un café d&apos;exception à Dakar et Thiès.
          </p>
        </div>

        <EcommerceProductsProvider wp_query={wp_query}>
          <ProductsGrid />
        </EcommerceProductsProvider>
      </div>
    </section>;
}